import { routes } from "@bezoned/shared/routes.js";
import { Spinner } from "@fluentui/react-components";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { useAvatarMenu } from "../../../hooks/use-avatar-menu.js";
import { tenantRosterGet } from "../../../services/api.service.js";
import { useLayoutStore } from "../../../store/layout.store.js";
import { RosterSwitchConfirmDialog } from "./roster-switch-confirm-dialog.js";
import { RosterTable } from "./roster-table.js";

const ROSTER_QUERY_REFRESH_INTERVAL_MS = 30_000;

interface SelectedOffice {
  teamId: string;
  teamName: string;
}

export const RosterView = () => {
  const hidePersonRoster = useLayoutStore((state) => state.hidePersonRoster);
  const personRosterVisible = useLayoutStore(
    (state) => state.personRosterVisible
  );
  const rosterRootRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { showAvatarMenu } = useAvatarMenu();
  const [selectedOffice, setSelectedOffice] = useState<SelectedOffice | null>(
    null
  );
  const [isSwitchingOffice, setIsSwitchingOffice] = useState(false);
  const currentTeamId =
    new URLSearchParams(location.search).get("teamId") ?? undefined;

  const { data, error, isPending } = useQuery({
    enabled: personRosterVisible,
    queryFn: tenantRosterGet,
    queryKey: ["tenantRoster"],
    refetchInterval: personRosterVisible
      ? ROSTER_QUERY_REFRESH_INTERVAL_MS
      : false,
    refetchOnMount: "always",
    refetchOnWindowFocus: personRosterVisible,
    staleTime: ROSTER_QUERY_REFRESH_INTERVAL_MS,
  });

  const handleUserClick = useCallback(
    ({ anchorRect, msUserId }: { anchorRect: DOMRect; msUserId: string }) => {
      showAvatarMenu(
        msUserId,
        {
          x: anchorRect.right,
          y: anchorRect.top,
        },
        "name-cell"
      );
    },
    [showAvatarMenu]
  );

  const handleOfficeClick = useCallback(
    ({ teamId, teamName }: SelectedOffice) => {
      setSelectedOffice({ teamId, teamName });
    },
    []
  );

  const handleCancelSwitch = useCallback(() => {
    if (isSwitchingOffice) {
      return;
    }

    setSelectedOffice(null);
  }, [isSwitchingOffice]);

  const handleConfirmSwitch = useCallback(() => {
    if (!selectedOffice) {
      return;
    }

    const searchParams = new URLSearchParams(location.search);
    const teamId = searchParams.get("teamId");

    if (teamId === selectedOffice.teamId) {
      setSelectedOffice(null);
      return;
    }

    searchParams.set("teamId", selectedOffice.teamId);
    setIsSwitchingOffice(true);
    hidePersonRoster();
    setSelectedOffice(null);

    // Switching between offices inside the current office route needs a hard
    // reload so the office, Fluid, and presence providers reconnect cleanly.
    window.location.assign(
      `${routes.protected.office}?${searchParams.toString()}`
    );
  }, [hidePersonRoster, location.search, selectedOffice]);

  if (isPending) {
    return (
      <div
        className="flex h-full min-h-0 w-full flex-1 items-center justify-center"
        ref={rosterRootRef}
      >
        <Spinner label="Loading people..." size="medium" />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="flex h-full min-h-0 w-full flex-1 items-center justify-center pr-1"
        ref={rosterRootRef}
      >
        <p className="text-bzd-red-500">Failed to load. Please try again.</p>
      </div>
    );
  }

  return (
    <div
      className="flex h-full min-h-0 w-full flex-1 flex-col"
      ref={rosterRootRef}
    >
      <div className="flex h-full min-h-0 flex-col overflow-hidden">
        <div className="min-h-0 flex-1 overflow-y-auto">
          <RosterTable
            currentTeamId={currentTeamId}
            onOfficeClick={handleOfficeClick}
            onUserClick={handleUserClick}
            users={data?.users ?? []}
          />
        </div>
      </div>
      <RosterSwitchConfirmDialog
        confirming={isSwitchingOffice}
        mountNode={rosterRootRef.current}
        officeName={selectedOffice?.teamName ?? ""}
        onCancel={handleCancelSwitch}
        onConfirm={handleConfirmSwitch}
        open={selectedOffice !== null}
      />
    </div>
  );
};
